import { CalendarColumnCell } from "@tento-ui/components/calendar/calendar-column-cell";
import { $$ } from "@tento-ui/fastdom";
import { DateFragment, TimePoint } from "@tento-chrono";
import type { NewEventGhost } from "@tento-lona/sheets-ui/sheet/gestures/event-ghost.ts";
import { GestureInfo } from "@tento-lona/sheets-ui/sheet/gestures/gesture-info.ts";
import type { CalendarSheetColumn } from "./calendar-sheet-column.ts";
import type {
  CalendarDaysStrip,
  ColumnFragment,
  DaysStripColumn,
  SheetNamedChildren,
} from "./calendar-days-strip.ts";

/**
 * A days strip's new-event create ghost (the "pending" cell) (F11): paint it
 * into every day fragment the ghost overlaps, with its top/bottom shadow
 * counts, or clear it everywhere. Moved out of calendar-days-strip.ts
 * unchanged; existing-item drags go through the strip's span ghost instead.
 */

/** What painting the pending cell reads from the strip, at the time of the call. */
export type PendingCellContext = {
  readonly columns: readonly DaysStripColumn<SheetNamedChildren>[];
  readonly props: CalendarDaysStrip.Props;
  readonly deps: CalendarSheetColumn.Dependencies;
  readonly redrawShadow: (columnFrag: ColumnFragment<SheetNamedChildren>) => void;
  /** `CalendarDaysStrip.bindPendingCell` (passed in, so this module imports only the strip's types). */
  readonly bindPendingCell: typeof CalendarDaysStrip.bindPendingCell;
};

/** Paint the create ghost `e` into the days it overlaps (and remove it from the others). */
export function paintPendingCell(e: NewEventGhost, ctx: PendingCellContext): void {
  const vm: CalendarDaysStrip.PendingCellViewModel = {
    item: null,
    type: "new" as const,
    title: e.title ?? "Untitled",
    backgroundColor: e.backgroundColor ??
      ctx.deps.presentation.categories.itemOpt(e.category ?? null)?.colorOpt() ??
      "var(--gray)",
    cursor: "grab",
  };

  for (const { frags, dr, $day, subindex } of ctx.columns) {
    const showGhost = dr.date.overlapsExclusive(e.newTimeRange);
    if (!showGhost) $day.removeNamedChild("pending");

    for (const columnFrag of frags) {
      columnFrag.topShadowRefCount.pending = 0;
      columnFrag.bottomShadowRefCount.pending = 0;

      if (!columnFrag.frag) continue;
      if (!showGhost) continue;

      const windowed = new DateFragment.Windowed(
        columnFrag.frag,
        ctx.props.partialWindow,
        ctx.props.window,
      );

      const windowedUtc = windowed.applyAll().toUtc();
      const truncatedTime = windowed.truncate(e.newTimeRange);
      if (!truncatedTime) {
        columnFrag.$frag.removeNamedChild("pending");
        continue;
      }

      const overlapsTop = e.newTimeRange.start.mse < windowedUtc.start.mse;
      if (overlapsTop) columnFrag.topShadowRefCount.pending = 1;
      const overlapsBottom = e.newTimeRange.end.mse > windowedUtc.end.mse;
      if (overlapsBottom) columnFrag.bottomShadowRefCount.pending = 1;

      const $cell = columnFrag.$frag.$namedChildOrElse(
        "pending",
        CalendarColumnCell.basic,
      );
      $cell.$content().bindNullState();
      $cell.bindCallbacks(
        ctx.deps.gestureManager.asCellDelegate(
          new GestureInfo(
            ctx.props.rowId,
            vm.item,
            ctx.props.column.withSubindex(subindex),
            windowed, null,
          ),
        ),
        "pending",
      );

      const time = TimePoint.Range.fromTodRange(truncatedTime);
      const timeDisplay = e.newTimeRange.toTz(columnFrag.frag.tz);
      $$.mutate(() =>
        ctx.bindPendingCell($cell, {
          vm,
          time,
          timeDisplay,
          overlapsTop,
          overlapsBottom,
        }),
      );
    }

    for (const columnFrag of frags) ctx.redrawShadow(columnFrag);
  }
}

/** Remove the create ghost and its shadow counts from every day. */
export function clearPendingCell(
  columns: readonly DaysStripColumn<SheetNamedChildren>[],
  redrawShadow: (columnFrag: ColumnFragment<SheetNamedChildren>) => void,
): void {
  for (const { frags, $day } of columns) {
    $day.toggleModifying(false);
    $day.removeNamedChild("pending");
    for (const columnFrag of frags) {
      columnFrag.topShadowRefCount.pending = 0;
      columnFrag.bottomShadowRefCount.pending = 0;
      redrawShadow(columnFrag);
    }
  }
}
